import * as bookmarkConstants from "../../../../constants/bookmarkConstants";
import * as modalTypes from "../../../../constants/modalTypes";
import * as objectTypes from "../../../../constants/objectTypes";
import * as optionHelper from "../../../../helpers/optionHelper";
import * as priceHelper from "../../../../helpers/priceHelper";
import * as propertyConstants from "../../../../constants/propertyConstants";
import * as translationHelper from "../../../../helpers/translationHelper";

import {
    BaseInProductPackage,
    generateBaseInProductPackageData,
    generateBaseInProductPackageHeadings
} from "../../../../types/baseInProductPackage";
import React, { Component } from "react";

import { Bookmark } from "../../../../types/bookmark";
import { Currency } from "../../../../types/currency";
import { MenuItem } from "../../../../types/menu";
import { ModalParams } from "../../../../types/modalParams";
import { ModalProperties } from "../../../../types/modalProperties";
import { TableContainer } from "../../../Table";
import { TableHeading } from "../../../../types/tableHeading";

import { t as translate } from "react-i18nify";

type Props = {
    tableConstant: string;
    objectType: objectTypes.ObjectType;
    dataPrivileges: any;
    priceDataPrivileges: MenuItem | null;
    loading: boolean;
    buttonLoading: boolean;
    allList: Array<BaseInProductPackage>;
    activeList: Array<BaseInProductPackage>;
    availableCount: number | null;
    columnOrder: Array<propertyConstants.Property>;
    columnVisibility: Record<propertyConstants.Property, boolean>;
    columnWidth: Record<propertyConstants.Property, number>;
    offset: number;
    page: number;
    rowCount: number;
    rowCountCustom: boolean;
    search: string | null;
    searchParams: Record<propertyConstants.Property, string>;
    showFilterRow: boolean;
    showGlobalSearch: boolean;
    sortingAsc: boolean;
    sortingCriterion: propertyConstants.Property | null;
    totalCount: number | null;
    currency: Currency | null;
    optionPriceDefinition: string | null;
    optionPriceEnableCompanyCost: boolean;
    optionPriceEnablePriceGroups: boolean;
};

export class ZoneProductBasePackageTable extends Component<Props> {
    generateTableHeadings = (
        modalType: modalTypes.ModalType | null = null,
        isPropagating: boolean = false,
        isPriceBookmark: boolean = false
    ): Array<TableHeading> => {
        const pricePropertyList = priceHelper.getPriceProperties(
            this.props.objectType,
            modalType,
            this.props.priceDataPrivileges,
            this.props.optionPriceEnableCompanyCost,
            this.props.optionPriceEnablePriceGroups,
            this.props.optionPriceDefinition
        );
        const requiredList: Array<propertyConstants.Property> = [
            propertyConstants.PROPERTY_ID,
            propertyConstants.PROPERTY_FILL_PROC,
            propertyConstants.PROPERTY_PACKAGE_NAME
        ];
        const alwaysVisibleList: Array<propertyConstants.Property> = [propertyConstants.PROPERTY_NAME];
        let editableList: Array<propertyConstants.Property> = [
            propertyConstants.PROPERTY_SEARCH_NAME,
            propertyConstants.PROPERTY_PRINT_NAME,
            propertyConstants.PROPERTY_CODE,
            propertyConstants.PROPERTY_FILL_PROC,
            propertyConstants.PROPERTY_MORE_INFO,
            propertyConstants.PROPERTY_NAME,
            propertyConstants.PROPERTY_UUID,
            propertyConstants.PROPERTY_BARCODES,
            propertyConstants.PROPERTY_ALTERNATIVE_BARCODES
        ];
        let visibleList: Array<propertyConstants.Property> = [
            propertyConstants.PROPERTY_NAME,
            propertyConstants.PROPERTY_SEARCH_NAME,
            propertyConstants.PROPERTY_PRINT_NAME,
            propertyConstants.PROPERTY_CODE,
            propertyConstants.PROPERTY_NOMINAL_AMOUNT,
            propertyConstants.PROPERTY_FILL_VOLUME_ML,
            propertyConstants.PROPERTY_FILL_PROC,
            propertyConstants.PROPERTY_MORE_INFO,
            propertyConstants.PROPERTY_UUID,
            propertyConstants.PROPERTY_BARCODES,
            propertyConstants.PROPERTY_ALTERNATIVE_BARCODES,
            propertyConstants.PROPERTY_IMAGE_ID
        ];

        if (modalType !== null) {
            editableList.push(propertyConstants.PROPERTY_IMAGE_ID);
        }

        if (modalType === null) {
            visibleList.splice(9, 0, propertyConstants.PROPERTY_FILES);
            visibleList = visibleList.concat(pricePropertyList);
            editableList = editableList.concat(pricePropertyList);
        }

        if (isPropagating) {
            visibleList = [propertyConstants.PROPERTY_NAME, propertyConstants.PROPERTY_PACKAGE_NAME];
            editableList = [];
        }

        if (isPriceBookmark) {
            visibleList = pricePropertyList;
            editableList = pricePropertyList;

            if (
                this.props.activeList.length &&
                this.props.activeList[0][propertyConstants.PROPERTY_PRICE_GROUP_BARCODE_LIST]?.length
            ) {
                visibleList.splice(pricePropertyList.length, 0, propertyConstants.PROPERTY_DIVIDING_LINE);
                visibleList.splice(pricePropertyList.length, 0, propertyConstants.PROPERTY_PRICE_GROUP_BARCODE_LIST);
                editableList.splice(pricePropertyList.length, 0, propertyConstants.PROPERTY_PRICE_GROUP_BARCODE_LIST);
            }
        }

        let orderList = [...visibleList];

        if (modalType === null) {
            orderList = optionHelper.handleTableHeadingOrder(visibleList, this.props.columnOrder);
            visibleList = optionHelper.handleTableHeadingVisibility(visibleList, this.props.columnVisibility);
        }

        return generateBaseInProductPackageHeadings(
            this.props.objectType,
            orderList,
            visibleList,
            editableList,
            requiredList,
            alwaysVisibleList,
            this.props.columnWidth
        );
    };

    generateBookmarks = (modalType: modalTypes.ModalType): Array<Bookmark> => {
        return [
            new Bookmark(
                bookmarkConstants.BOOKMARK_GENERAL,
                bookmarkConstants.BOOKMARK_TYPE_FORM,
                translate("general.general"),
                this.generateTableHeadings(modalType),
                this.props.dataPrivileges, // TODO own privilege
                false
            ),
            new Bookmark(
                bookmarkConstants.BOOKMARK_PRICES,
                bookmarkConstants.BOOKMARK_TYPE_FORM,
                translate("prices.prices"),
                this.generateTableHeadings(modalType, false, true),
                this.props.dataPrivileges, // TODO own privilege
                false
            ),
            new Bookmark(
                bookmarkConstants.BOOKMARK_FILES,
                bookmarkConstants.BOOKMARK_TYPE_TABLE,
                translate("product.productFiles"),
                [],
                this.props.dataPrivileges, // TODO own privilege
                false
            )
        ];
    };

    generateAddModalParams = (): ModalParams => {
        return new ModalParams(
            modalTypes.PROPAGATE_MODAL,
            objectTypes.SYSTEM_ZONE_BASE_IN_PRODUCT_PACKAGE,
            "",
            [],
            this.generateTableHeadings(modalTypes.MODAL_ZONE_BASE_IN_PRODUCT_PACKAGES_PROPAGATE, true),
            undefined,
            this.generateTableHeadings(modalTypes.MODAL_ZONE_BASE_IN_PRODUCT_PACKAGES_PROPAGATE, true),
            undefined,
            undefined,
            this.props.dataPrivileges,
            undefined,
            "simple-table"
        );
    };

    generateEditModalParams = (): ModalParams | null => {
        const activeData = generateBaseInProductPackageData(this.props.activeList);

        if (activeData.length) {
            return new ModalParams(
                modalTypes.EDIT_MODAL,
                this.props.objectType,
                translate("product.editBaseInProductPackage", {
                    packageName: activeData[0][propertyConstants.PROPERTY_NAME]
                }),
                activeData[0],
                [],
                this.generateBookmarks(modalTypes.MODAL_BASE_IN_PRODUCT_PACKAGE_EDIT),
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                this.props.currency
            );
        }

        return null;
    };

    render(): JSX.Element {
        const modalProperties: ModalProperties = {
            addModalType: modalTypes.MODAL_ZONE_BASE_IN_PRODUCT_PACKAGES_PROPAGATE,
            addModalParams: this.generateAddModalParams(),
            editModalType: modalTypes.MODAL_BASE_IN_PRODUCT_PACKAGE_EDIT,
            editModalParams: this.generateEditModalParams()
        };

        return (
            <div className="height-100">
                <TableContainer
                    className={"table-with-filter"}
                    tableConstant={this.props.tableConstant}
                    objectType={this.props.objectType}
                    dataPrivileges={this.props.dataPrivileges}
                    loading={this.props.loading}
                    buttonLoading={this.props.buttonLoading}
                    title={translationHelper.getTitleSectionTranslation(this.props.objectType)}
                    titleClassName={"title"}
                    headings={this.generateTableHeadings()}
                    data={generateBaseInProductPackageData(this.props.allList)}
                    activeData={this.props.activeList}
                    availableCount={this.props.availableCount}
                    modalProperties={modalProperties}
                    offset={this.props.offset}
                    page={this.props.page}
                    rowCount={this.props.rowCount}
                    rowCountCustom={this.props.rowCountCustom}
                    search={this.props.search}
                    searchParams={this.props.searchParams}
                    showFilterRow={this.props.showFilterRow}
                    showGlobalSearch={this.props.showGlobalSearch}
                    sortingAsc={this.props.sortingAsc}
                    sortingCriterion={this.props.sortingCriterion}
                    totalCount={this.props.totalCount}
                    currency={this.props.currency}
                />
            </div>
        );
    }
}
